import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "button"]
  static values = {
    teamId: Number,
    quickCreateUrl: String
  }

  declare readonly fileInputTarget: HTMLInputElement
  declare readonly buttonTarget: HTMLButtonElement
  declare readonly teamIdValue: number
  declare readonly quickCreateUrlValue: string

  openFileDialog() {
    this.fileInputTarget.click()
  }

  async handleFileSelect(event) {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.geojson') && file.type !== 'application/geo+json' && file.type !== 'application/json') {
      alert('Please select a valid GeoJSON file (.geojson)')
      return
    }

    // Disable button during upload
    this.buttonTarget.disabled = true
    const originalText = this.buttonTarget.innerHTML
    this.buttonTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i>Loading...'

    try {
      // Read file content
      const fileContent = await file.text()

      // Validate JSON
      let geojsonData
      try {
        geojsonData = JSON.parse(fileContent)
      } catch (e) {
        throw new Error('Invalid JSON file')
      }

      // Extract name from filename (remove .geojson extension)
      const fileName = file.name
      const annotationName = fileName.toLowerCase().endsWith('.geojson') 
        ? fileName.slice(0, -8) // Remove '.geojson' (8 characters)
        : fileName

      // Create new annotation
      const csrfToken = this.getCSRFToken()
      
      if (!csrfToken) {
        throw new Error('CSRF token not found. Please refresh the page and try again.')
      }
      
      if (!this.quickCreateUrlValue) {
        throw new Error('Quick create URL not found. Please refresh the page and try again.')
      }
      
      // Use FormData to submit as a form POST (matches Rails button_to behavior)
      const formData = new FormData()
      formData.append('authenticity_token', csrfToken)
      // quick_create doesn't need any other parameters
      
      let createResponse
      try {
        createResponse = await fetch(this.quickCreateUrlValue, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken
            // Don't set Content-Type - browser will set it automatically with boundary for FormData
          },
          body: formData,
          credentials: 'same-origin'
          // Let fetch handle redirects automatically (default behavior)
        })
      } catch (fetchError) {
        // Network error or fetch was aborted
        if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
          throw new Error(`Network error: Unable to connect to server. Please check your connection and try again.`)
        }
        throw new Error(`Request failed: ${fetchError.message}`)
      }

      // Check if response status is 0 (usually means request was blocked or aborted)
      if (createResponse.status === 0) {
        throw new Error('Request was blocked or aborted. This may be due to a CORS issue, network problem, or browser security setting.')
      }

      if (!createResponse.ok) {
        const errorText = await createResponse.text().catch(() => 'Unable to read error response')
        throw new Error(`Failed to create annotation: HTTP ${createResponse.status} ${createResponse.statusText} - ${errorText.substring(0, 200)}`)
      }

      // Get the final URL after redirects (fetch follows redirects automatically)
      const redirectUrl = createResponse.url
      
      if (!redirectUrl) {
        throw new Error(`Could not determine redirect URL. Response status: ${createResponse.status}`)
      }
      // Get the annotation ID from the redirect URL
      // Try both /paintbrush/:id and /teams/:team_id/paintbrush/:id patterns
      const annotationIdMatch = redirectUrl.match(/\/paintbrush\/(\d+)/) || redirectUrl.match(/\/annotations\/(\d+)/)
      
      if (!annotationIdMatch) {
        // Try to extract ID from URL anyway - might be in a different format
        // If we can't find it, we can't clean up, but at least we'll show an error
        throw new Error('Could not determine annotation ID from redirect URL: ' + redirectUrl)
      }

      const annotationId = annotationIdMatch[1]

      // Update annotation name from filename
      const updateUrl = `/paintbrush/${annotationId}`
      const updateFormData = new FormData()
      updateFormData.append('annotation[name]', annotationName)
      
      const updateResponse = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: updateFormData,
        credentials: 'same-origin'
      })

      if (!updateResponse.ok) {
        // If name update fails, still try to upload GeoJSON, but log the error
        console.warn(`Failed to update annotation name: HTTP ${updateResponse.status}`)
      }

      // Upload GeoJSON to the annotation
      // Note: annotations routes are shallow, so member routes don't include team_id (see paintbrush_app.tsx)
      const saveGeojsonUrl = `/paintbrush/${annotationId}/save_geojson`
      let uploadResponse
      try {
        // Use FormData to match how save_geojson expects the data (see paintbrush_app.tsx)
        const geojsonFormData = new FormData()
        geojsonFormData.set('geojson', fileContent)
        
        uploadResponse = await fetch(saveGeojsonUrl, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
            // Don't set Content-Type - browser will set it automatically with boundary for FormData
          },
          body: geojsonFormData,
          credentials: 'same-origin'
        })

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text()
          // Delete the annotation we just created since upload failed
          await this.deleteAnnotation(annotationId)
          throw new Error(`Failed to upload GeoJSON: ${errorText}`)
        }
      } catch (uploadError) {
        // If upload fails for any reason, delete the annotation we created
        if (annotationId) {
          await this.deleteAnnotation(annotationId).catch(deleteError => {
            console.error('Failed to delete annotation after upload error:', deleteError)
          })
        }
        throw uploadError
      }

      // Redirect to the annotation page
      // Make sure redirectUrl is absolute
      if (redirectUrl.startsWith('/')) {
        window.location.href = window.location.origin + redirectUrl
      } else {
        window.location.href = redirectUrl
      }

    } catch (error) {
      console.error('Error loading GeoJSON:', error)
      alert(`Error loading GeoJSON: ${error.message}`)
      this.buttonTarget.disabled = false
      this.buttonTarget.innerHTML = originalText
    } finally {
      // Reset file input
      this.fileInputTarget.value = ''
    }
  }

  async deleteAnnotation(annotationId) {
    const csrfToken = this.getCSRFToken()
    // Note: annotations routes are shallow, so member routes don't include team_id
    const deleteUrl = `/paintbrush/${annotationId}`
    
    try {
      const deleteResponse = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      })
      
      if (!deleteResponse.ok) {
        console.warn(`Failed to delete annotation ${annotationId}: HTTP ${deleteResponse.status}`)
      }
    } catch (error) {
      console.error(`Error deleting annotation ${annotationId}:`, error)
      // Don't throw - we don't want to mask the original error
    }
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }
}
